<template>
    <!--
        The one text input for user-authored content: notes and add-on bodies.
        Callers always mount this component; which editor renders is an internal
        decision, taken from the profile and the host's addon_rich_text_editor
        tenant flag:

        - the note profile follows the flag. A note is kept as plain text as
          well as markup, so a tenant with the flag off writes notes in the
          textarea and no note loses anything.
        - the add-on profile is always rich. An add-on body is markup and
          nothing else, so a textarea there could only flatten the links,
          lists, images and embeds an editorial piece is made of.

        The plain editor is a textarea whose box grows with the text: an
        invisible mirror holding the same text sizes the grid row and the field
        stretches to fill it, so no keystroke has to measure anything. min-w-0
        keeps an unbreakable word (a pasted URL) from sizing the row to the word
        instead of wrapping it.

        The rich editor is Tiptap, emitting HTML. Its extension set, built in
        js/tiptap/richTextExtensions, mirrors the server profile in Farfalla
        (tiptap-php). The server stays the canonical sanitizer, so anything
        outside it is dropped there and reported back.

        Swapping the rich implementation later means touching only this file
        and its js/tiptap modules.

        The adornment slot is the bottom-right corner of the box (the note
        editor puts its dictation button there). While it is used, the field
        leaves that corner clear so a long text never runs underneath it.
    -->
    <div class="relative grid rounded-lg border border-theme-border bg-theme-bg">
        <template v-if="!rich">
            <pre
                aria-hidden="true"
                class="invisible min-h-[96px] min-w-0 whitespace-pre-wrap break-words px-3 pt-2 text-base leading-6 [font-family:inherit] [grid-area:1/1]"
                :class="bottomPaddingClass"
                >{{ value + '\n' }}</pre
            >
            <textarea
                ref="textarea"
                :value="value"
                v-bind="$attrs"
                rows="1"
                :aria-label="ariaLabel"
                :placeholder="placeholder"
                class="min-w-0 resize-none overflow-hidden rounded-lg border-none bg-transparent px-3 pt-2 text-left text-base font-normal leading-6 text-theme-text placeholder-theme-toolbar-icon outline-none [grid-area:1/1] focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-theme-accent"
                :class="bottomPaddingClass"
                @input="$emit('input', $event.target.value)"
            ></textarea>
        </template>

        <div v-else class="min-w-0 [grid-area:1/1]">
            <div
                class="flex flex-wrap items-center gap-1 border-b border-theme-border px-2 py-1"
                role="toolbar"
                :aria-label="labels.toolbar"
            >
                <button
                    v-for="control in toolbarControls"
                    :key="control.key"
                    type="button"
                    :aria-label="control.label"
                    :title="control.label"
                    :aria-pressed="control.isActive() ? 'true' : 'false'"
                    :disabled="htmlMode"
                    class="flex h-8 w-8 items-center justify-center rounded text-sm focus-visible:ring-2 focus-visible:ring-inset disabled:pointer-events-none disabled:opacity-40"
                    :class="buttonState(control.isActive())"
                    @click="control.run()"
                >
                    <svg-icon v-if="control.icon" :icon="control.icon" width="18" height="18" fill="currentColor" />
                    <span v-else aria-hidden="true" :class="control.glyphClass">{{ control.glyph }}</span>
                </button>
                <span class="mx-1 h-5 w-px bg-theme-border" aria-hidden="true"></span>
                <button
                    v-if="profile === 'addon'"
                    type="button"
                    :aria-label="labels.image"
                    :title="labels.image"
                    :disabled="htmlMode"
                    class="flex h-8 w-8 items-center justify-center rounded focus-visible:ring-2 focus-visible:ring-inset disabled:pointer-events-none disabled:opacity-40"
                    :class="buttonState(false)"
                    @click="insertImage"
                >
                    <svg-icon icon="image" width="18" height="18" fill="currentColor" />
                </button>
                <button
                    type="button"
                    :aria-label="labels.html"
                    :title="labels.html"
                    :aria-pressed="htmlMode"
                    class="flex h-8 items-center justify-center rounded px-2 text-xs font-medium tracking-wide focus-visible:ring-2 focus-visible:ring-inset"
                    :class="buttonState(htmlMode)"
                    @click="toggleHtmlMode"
                >
                    <span aria-hidden="true">HTML</span>
                </button>
            </div>
            <!-- Escape and Enter end here: the note editor around this input closes on
                 Escape, and the add-on editor around it is a form Enter would submit. -->
            <div
                v-if="urlEntry"
                class="flex flex-wrap items-center gap-2 border-b border-theme-border px-2 py-1.5 text-xs text-theme-text"
                @keydown.esc.stop.prevent="leaveUrlEntry"
            >
                <input
                    ref="urlInput"
                    v-model="urlDraft"
                    type="url"
                    inputmode="url"
                    autocomplete="off"
                    spellcheck="false"
                    placeholder="https://"
                    :aria-label="urlEntryLabels.field"
                    class="min-w-0 flex-1 rounded border border-theme-border bg-theme-bg px-2 py-1 text-sm text-theme-text placeholder-theme-toolbar-icon outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-theme-accent"
                    @keydown.enter.prevent="submitUrlEntry"
                />
                <button
                    type="button"
                    data-url-entry="apply"
                    class="rounded border border-theme-border px-2 py-1 font-medium focus-visible:ring-2 focus-visible:ring-inset"
                    :class="buttonState(false)"
                    @click="submitUrlEntry"
                >
                    {{ urlEntryLabels.apply }}
                </button>
                <button
                    type="button"
                    data-url-entry="cancel"
                    class="rounded px-2 py-1 focus-visible:ring-2 focus-visible:ring-inset"
                    :class="quietButtonState"
                    @click="leaveUrlEntry"
                >
                    {{ localizations[lang].cancel }}
                </button>
            </div>
            <EditorContent
                v-show="!htmlMode"
                :editor="editor"
                v-bind="$attrs"
                class="rich-text-input__content font-noto-sans text-base leading-6 text-theme-text"
                @keydown.native.capture="onEmbedOfferKeydown"
            />
            <textarea
                v-if="htmlMode"
                v-model="htmlDraft"
                :aria-label="labels.html"
                spellcheck="false"
                class="font-mono block min-h-[96px] w-full resize-y rounded-b-lg border-none bg-transparent px-3 pt-2 text-sm leading-6 text-theme-text outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-theme-accent"
                :class="bottomPaddingClass"
                @input="$emit('input', htmlDraft)"
            ></textarea>
            <div
                v-if="pendingEmbed"
                class="flex flex-wrap items-center gap-2 border-t border-theme-border px-3 py-2 text-xs text-theme-text"
                :class="{ 'pr-12': $slots.adornment }"
                role="status"
                aria-live="polite"
            >
                <span class="min-w-0 flex-1">{{ pendingEmbedLabels.offer.replace('{host}', pendingEmbedHost) }}</span>
                <button
                    type="button"
                    class="rounded border border-theme-border px-2 py-1 font-medium focus-visible:ring-2 focus-visible:ring-inset"
                    :class="buttonState(false)"
                    @click="confirmPendingEmbed"
                >
                    {{ pendingEmbedLabels.insert }}
                </button>
                <button
                    type="button"
                    class="rounded px-2 py-1 focus-visible:ring-2 focus-visible:ring-inset"
                    :class="quietButtonState"
                    @click="keepPendingAsLink"
                >
                    {{ labels.embed.keepLink }}
                </button>
            </div>
            <div
                v-if="noticeLines.length && !noticeDismissed"
                class="flex items-start gap-2 border-t border-theme-border px-3 py-2 text-xs text-theme-text"
                :class="{ 'pr-12': $slots.adornment }"
                role="status"
                aria-live="polite"
            >
                <div class="min-w-0 flex-1">
                    <p class="font-medium">{{ labels.discard.intro }}</p>
                    <ul class="mt-0.5 list-disc pl-4">
                        <li v-for="line in noticeLines" :key="line">{{ line }}</li>
                    </ul>
                </div>
                <button
                    type="button"
                    :aria-label="labels.discard.dismiss"
                    class="flex h-6 w-6 shrink-0 items-center justify-center rounded focus-visible:ring-2 focus-visible:ring-inset"
                    :class="quietButtonState"
                    @click="noticeDismissed = true"
                >
                    <span aria-hidden="true">&times;</span>
                </button>
            </div>
        </div>

        <slot name="adornment" />
    </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { describeDiscards } from '../../js/utils/richTextDiscards';
import { embedHostAllowed, embedSrcFor, singlePastedUrl } from '../../js/utils/richTextEmbeds';
import { hasSafeImageSource, isHttps, refusedSources, schemeOf } from '../../js/utils/richTextSources';

/**
 * Whether the document holds anything worth saving: text, or a media node such
 * as an image or an embed. An empty paragraph, or paragraphs of nothing but
 * whitespace, do not — the caller's "has annotation" check must see them as empty.
 * @param {import('@tiptap/core').Editor} editor - The live editor
 * @returns {boolean}
 */
function editorHasContent(editor) {
    if (editor.isEmpty) {
        return false;
    }

    if (editor.getText().trim() !== '') {
        return true;
    }

    let hasMedia = false;
    editor.state.doc.descendants(node => {
        if (node.isAtom && !node.isText && node.type.name !== 'hardBreak') {
            hasMedia = true;
        }

        return !hasMedia;
    });

    return hasMedia;
}

/**
 * Wrappers the schema unwraps without losing anything; not worth a notice line.
 */
const SILENT_WRAPPERS = [
    'html',
    'head',
    'body',
    'div',
    'span',
    'section',
    'article',
    'header',
    'footer',
    'main',
    'center',
    'font',
    'meta',
    'link',
];

/**
 * Tags the note profile keeps. A video's <source> is kept as its address.
 */
const KEPT_TAGS = [
    'p',
    'br',
    'strong',
    'b',
    'em',
    'i',
    'u',
    's',
    'del',
    'strike',
    'ul',
    'ol',
    'li',
    'a',
    'iframe',
    'video',
    'source',
];

/**
 * How every button the editor draws shows its state, kept in one place so the
 * toolbar, the offer bar and the notice cannot drift apart. The border colour
 * is never a fill: high contrast themes set it to the text colour, which would
 * bury the glyph. A pressed button takes the accent and the ink each theme
 * pairs with it, and its ring inverts so it does not vanish into the fill.
 */
const BUTTON_STATES = {
    idle: 'text-theme-text hover:bg-theme-accent-bg hover:text-theme-accent focus-visible:ring-theme-accent',
    pressed: 'bg-theme-accent text-theme-accent-on focus-visible:ring-theme-accent-on',
    quiet: 'text-theme-toolbar-icon hover:bg-theme-accent-bg hover:text-theme-accent focus-visible:ring-theme-accent',
};

/**
 * Tags the add-on profile keeps.
 */
// An h1 is kept too, one level down.
const ADD_ON_KEPT_TAGS = [...KEPT_TAGS, 'img', 'h1', 'h2', 'h3', 'h4', 'blockquote', 'aside', 'figure', 'figcaption'];

export default {
    name: 'RichTextInput',
    components: {
        // Tiptap loads on demand: every import of it in this file is dynamic,
        // so Rollup splits the whole editor into its own chunk, fetched only
        // where one renders rather than by everything that mounts this input.
        EditorContent: () => import('@tiptap/vue-2').then(module => module.EditorContent),
    },
    inheritAttrs: false,
    props: {
        value: {
            type: String,
            default: '',
        },
        /**
         * Which content contract this input produces: 'note' (formatting,
         * links, video embeds, hosted video) or 'addon' (also headings,
         * quotes, boxes, images and the whole embed allowlist). Must stay in
         * lockstep with the RichTextProfile the server applies.
         */
        profile: {
            type: String,
            default: 'note',
            validator: profile => ['note', 'addon'].includes(profile),
        },
        /**
         * Whether to render the rich editor rather than the plain textarea.
         * The caller answers it, because the answer follows how the
         * caller stores the value: a note keeps a plain column
         * beside the markup and follows the tenant flag,
         * an add-on body has markup alone.
         */
        rich: {
            type: Boolean,
            default: false,
        },
        ariaLabel: {
            type: String,
            default: '',
        },
        placeholder: {
            type: String,
            default: '',
        },
        autofocus: {
            type: Boolean,
            default: false,
        },
        /**
         * Discards reported by the save response (sanitization.discarded[]
         * from Farfalla): objects shaped {node, reason, detail}. Rendered in
         * the same notice as the local paste detection — the save always
         * completes, this only tells the operator what will not be kept and why.
         */
        discards: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            editor: null,
            // Resolves once the editor chunk is loaded and the instance
            // exists; tests and focus() await it instead of counting ticks.
            editorReady: null,
            pasteDiscards: [],
            noticeDismissed: false,
            // Source-editing mode (the "HTML" toolbar toggle): the operator
            // edits the raw HTML and toggling back renders it as preview.
            htmlMode: false,
            htmlDraft: '',
            // A just-pasted provider URL waiting for the operator's choice:
            // {url, from, to} while the offer bar is visible.
            pendingEmbed: null,
            // Which URL the row under the toolbar is asking for: 'link', 'image' or null.
            urlEntry: null,
            urlDraft: '',
        };
    },
    computed: {
        ...mapState(['localizations', 'lang']),
        ...mapGetters(['richTextEmbedHosts']),

        labels() {
            return this.localizations[this.lang].richText;
        },
        /**
         * The corner stays clear only while something sits in it. Slot
         * presence does not change while the input is mounted, so reading it
         * once is enough.
         */
        bottomPaddingClass() {
            return this.$slots.adornment ? 'pb-9' : 'pb-2';
        },
        /**
         * The hosts this profile may embed: notes take video and audio
         * providers, add-on bodies take the whole allowlist.
         */
        allowedEmbedHosts() {
            const hosts = this.richTextEmbedHosts ?? {};

            return this.profile === 'addon' ? (hosts.all ?? []) : [...(hosts.video ?? []), ...(hosts.audio ?? [])];
        },
        /**
         * The hosts whose embeds are players with no picture. Kept separate so
         * the editor can name them for what they are and size them as such.
         */
        audioEmbedHosts() {
            return this.richTextEmbedHosts?.audio ?? [];
        },
        /**
         * An audio embed is offered in its own words: "insert the video from
         * soundcloud.com" reads as a mistake to whoever just pasted a track.
         */
        pendingEmbedLabels() {
            const { offer, insert, offerAudio, insertAudio } = this.labels.embed;

            return this.pendingEmbed?.kind === 'audio'
                ? { offer: offerAudio ?? offer, insert: insertAudio ?? insert }
                : { offer, insert };
        },
        pendingEmbedHost() {
            if (!this.pendingEmbed) {
                return '';
            }

            return new URL(this.pendingEmbed.url).hostname.replace(/^www\./, '');
        },
        /**
         * Human-readable notice lines, merging server-reported discards with
         * the local paste detection, deduplicated.
         */
        noticeLines() {
            return describeDiscards([...this.discards, ...this.pasteDiscards], this.labels.discard);
        },
        quietButtonState() {
            return BUTTON_STATES.quiet;
        },
        urlEntryLabels() {
            return this.urlEntry === 'image'
                ? { field: this.labels.imagePrompt, apply: this.labels.image }
                : { field: this.labels.linkPrompt, apply: this.labels.link };
        },
        toolbarControls() {
            return [
                {
                    key: 'bold',
                    label: this.labels.bold,
                    glyph: 'B',
                    glyphClass: 'font-bold',
                    isActive: () => this.editor?.isActive('bold') ?? false,
                    run: () => this.editor.chain().focus().toggleBold().run(),
                },
                {
                    key: 'italic',
                    label: this.labels.italic,
                    glyph: 'I',
                    glyphClass: 'italic',
                    isActive: () => this.editor?.isActive('italic') ?? false,
                    run: () => this.editor.chain().focus().toggleItalic().run(),
                },
                {
                    key: 'underline',
                    label: this.labels.underline,
                    glyph: 'U',
                    glyphClass: 'underline',
                    isActive: () => this.editor?.isActive('underline') ?? false,
                    run: () => this.editor.chain().focus().toggleUnderline().run(),
                },
                {
                    key: 'strike',
                    label: this.labels.strikethrough,
                    glyph: 'S',
                    glyphClass: 'line-through',
                    isActive: () => this.editor?.isActive('strike') ?? false,
                    run: () => this.editor.chain().focus().toggleStrike().run(),
                },
                ...this.addOnBlockControls,
                {
                    key: 'bulletList',
                    label: this.labels.bulletList,
                    icon: 'ic_list_white_24px',
                    isActive: () => this.editor?.isActive('bulletList') ?? false,
                    run: () => this.editor.chain().focus().toggleBulletList().run(),
                },
                {
                    key: 'orderedList',
                    label: this.labels.orderedList,
                    icon: 'ic_format_list_numbered_white_24px',
                    isActive: () => this.editor?.isActive('orderedList') ?? false,
                    run: () => this.editor.chain().focus().toggleOrderedList().run(),
                },
                {
                    key: 'link',
                    label: this.editor?.isActive('link') ? this.labels.unlink : this.labels.link,
                    icon: 'baseline-link-24px',
                    isActive: () => this.editor?.isActive('link') ?? false,
                    run: () => this.toggleLink(),
                },
            ];
        },
        /**
         * The blocks only an add-on body keeps. Its headings start at h2,
         * under the piece's own title; an h1 a body holds is read as an h2,
         * as the server reads it.
         */
        addOnBlockControls() {
            if (this.profile !== 'addon') {
                return [];
            }

            return [
                ...[2, 3, 4].map(level => ({
                    key: `heading${level}`,
                    label: this.labels.heading.replace('{level}', level),
                    glyph: `H${level}`,
                    glyphClass: 'font-bold',
                    isActive: () => this.editor?.isActive('heading', { level }) ?? false,
                    run: () => this.editor.chain().focus().toggleHeading({ level }).run(),
                })),
                {
                    key: 'blockquote',
                    label: this.labels.blockquote,
                    icon: 'quote-24px',
                    isActive: () => this.editor?.isActive('blockquote') ?? false,
                    run: () => this.editor.chain().focus().toggleWrapper('blockquote').run(),
                },
                {
                    key: 'aside',
                    label: this.labels.aside,
                    icon: 'aside-24px',
                    isActive: () => this.editor?.isActive('aside') ?? false,
                    run: () => this.editor.chain().focus().toggleWrapper('aside').run(),
                },
            ];
        },
    },
    watch: {
        // A fresh server report reopens a dismissed notice.
        discards() {
            this.noticeDismissed = false;
        },
        value(newValue) {
            // While the operator edits raw source, the emitted draft must not
            // round-trip into the editor until the toggle applies it.
            if (this.htmlMode) {
                return;
            }
            if (newValue === this._lastEmitted) {
                return;
            }
            // An external writer changed the bound value: sync the editor only
            // when the change did not originate in it (or the cursor would
            // jump on every keystroke), and re-emit the normalized HTML so
            // what gets saved is the document the user sees.
            if (this.editor && newValue !== this.editor.getHTML() && !(newValue === '' && this.editor.isEmpty)) {
                this.registerIncomingHtml(newValue);
                this.editor.commands.setContent(newValue, false);
                this.emitDocument();
            }
        },
        rich: {
            immediate: true,
            handler(rich) {
                if (rich) {
                    this.$nextTick(() => this.createEditor());
                } else {
                    this.destroyEditor();
                }
            },
        },
    },
    // The last string emitted from this editor, kept off the reactive data so a
    // value handed straight back does not cost a second serialization of the
    // document to discover it is the one already on screen.
    created() {
        this._lastEmitted = null;
        // The plain textarea would flatten the lists, links and images an
        // add-on body keeps, and there is no second column to recover them
        // from, so the pair is a mistake rather than a configuration.
        if (this.profile === 'addon' && !this.rich) {
            throw new Error('RichTextInput: an add-on body must set `rich`, because its markup is all it stores.');
        }
    },
    mounted() {
        if (this.autofocus) {
            this.focus();
        }
    },
    beforeDestroy() {
        this.destroyEditor();
    },
    methods: {
        /**
         * @param {boolean} pressed - Whether the button's toggle is on
         * @returns {string}
         */
        buttonState(pressed) {
            return pressed ? BUTTON_STATES.pressed : BUTTON_STATES.idle;
        },
        /**
         * Public: callers focus the input without knowing which editor renders.
         * @param {{ preventScroll?: boolean }} [options] - Same shape as HTMLElement.focus()
         */
        focus(options = {}) {
            this.$nextTick(async () => {
                await this.editorReady;

                if (this.editor) {
                    this.editor.commands.focus('end', { scrollIntoView: !options.preventScroll });
                } else if (this.$refs.textarea) {
                    this.$refs.textarea.focus(options);
                }
            });
        },
        /**
         * Public: appends text composed outside the input (dictation) at the
         * end of the document. It lands as text, never parsed as markup, so a
         * dictated sentence becomes words in the last paragraph instead of a
         * string glued after the closing tag.
         * @param {string} text - Plain text to append
         */
        async insertText(text) {
            if (!text) {
                return;
            }

            await this.editorReady;

            // While the source is being edited, the source is the document.
            if (this.htmlMode) {
                this.htmlDraft = `${this.htmlDraft}${text}`;
                this.$emit('input', this.htmlDraft);
                return;
            }

            if (this.editor) {
                this.editor.chain().focus('end').insertContent({ type: 'text', text }).run();
                return;
            }

            this.$emit('input', `${this.value}${text}`);
        },
        createEditor() {
            if (!this.editorReady) {
                this.editorReady = this.loadEditor();
            }

            return this.editorReady;
        },
        async loadEditor() {
            const [{ Editor }, { richTextExtensions }] = await Promise.all([
                import('@tiptap/vue-2'),
                import('../../js/tiptap/richTextExtensions'),
            ]);

            // `rich` may have turned false while the chunk downloaded. The
            // watcher already ran destroyEditor by then and
            // nothing should come back.
            if (this.editor || !this.rich) {
                return;
            }

            // A body written elsewhere, by Trix in the dashboard or over the
            // API, may hold what this schema drops: once parsed it is gone,
            // and a save sends the server nothing to report. The notice is
            // the one place the writer learns what an edit would lose.
            this.registerIncomingHtml(this.value);

            this.editor = new Editor({
                content: this.value,
                extensions: richTextExtensions(this.profile, {
                    allowedSrc: src => embedHostAllowed(src, this.allowedEmbedHosts),
                    placeholder: () => this.placeholder,
                }),
                editorProps: {
                    attributes: {
                        'aria-label': this.ariaLabel,
                        class: `min-h-[96px] break-words px-3 pt-2 ${this.bottomPaddingClass} outline-none`,
                    },
                    // The schema silently drops pasted markup it cannot represent; detect
                    // it here so the operator is told in the moment. The HTML itself passes
                    // through unchanged — dropping is the schema's job.
                    transformPastedHTML: html => this.registerIncomingHtml(html),
                    handlePaste: (view, event) => this.handlePastedText(view, event),
                },
                onUpdate: () => this.emitDocument(),
                onTransaction: ({ transaction }) => this.trackPendingEmbed(transaction),
            });
        },
        destroyEditor() {
            this.editor?.destroy();
            this.editor = null;
            this.editorReady = null;
            this._lastEmitted = null;
        },
        /**
         * A blank document — nothing but empty paragraphs or whitespace —
         * emits '' instead of its markup, so "has annotation" checks keep
         * working unchanged.
         */
        emitDocument() {
            this._lastEmitted = editorHasContent(this.editor) ? this.editor.getHTML() : '';
            this.$emit('input', this._lastEmitted);
        },
        /**
         * Collects what the editor schema will drop of markup handed to it, so
         * the notice can name it. Wrappers that unwrap harmlessly are not
         * worth noise.
         * @param {string} html - The incoming markup, returned untouched
         * @returns {string}
         */
        registerIncomingHtml(html) {
            const keptTags = this.profile === 'addon' ? ADD_ON_KEPT_TAGS : KEPT_TAGS;

            const pasted = new DOMParser().parseFromString(html, 'text/html');
            const droppedTags = [...pasted.querySelectorAll('*')]
                .map(element => element.tagName.toLowerCase())
                .filter(tag => !keptTags.includes(tag) && !SILENT_WRAPPERS.includes(tag));

            const additions = [...new Set(droppedTags)].map(tag => ({
                node: tag,
                reason: 'tag_not_allowed',
                detail: tag,
            }));

            // Media the schema drops for its address: http, relative or a
            // scheme that is not a picture. Named by scheme, as the server does.
            const media = ['img', 'video', 'iframe'].filter(tag => keptTags.includes(tag));
            refusedSources(pasted, media).forEach(({ node, scheme }) => {
                additions.push({ node, reason: 'unsafe_url', detail: scheme });
            });

            // A handler or srcdoc never survives the schema; the server
            // reports it as unsafe_attribute when it sees one, so the notice
            // says the same before the schema drops it in silence.
            for (const element of pasted.querySelectorAll('*')) {
                for (const { name } of element.attributes) {
                    const attribute = name.toLowerCase();

                    if (attribute.startsWith('on') || attribute === 'srcdoc') {
                        additions.push({
                            node: element.tagName.toLowerCase(),
                            reason: 'unsafe_attribute',
                            detail: attribute,
                        });
                    }
                }
            }

            // An https iframe survives the schema only when its host is
            // allowlisted; the rejected ones deserve the specific "provider not
            // allowed" line rather than a generic unsupported-tag one.
            [...pasted.querySelectorAll('iframe[src]')]
                .filter(iframe => isHttps(iframe.getAttribute('src')))
                .filter(iframe => !embedHostAllowed(iframe.getAttribute('src'), this.allowedEmbedHosts))
                .forEach(iframe => {
                    let host;
                    try {
                        host = new URL(iframe.getAttribute('src')).hostname;
                    } catch {
                        host = iframe.getAttribute('src');
                    }
                    additions.push({ node: 'iframe', reason: 'embed_domain_not_allowed', detail: host });
                });

            this.addPasteDiscards(additions);

            return html;
        },
        /**
         * @param {Array<{node: string, reason: string, detail: string|null}>} discards
         */
        addPasteDiscards(discards) {
            const known = new Set(this.pasteDiscards.map(discard => `${discard.reason}|${discard.detail}`));
            const fresh = discards.filter(discard => !known.has(`${discard.reason}|${discard.detail}`));

            if (fresh.length) {
                this.pasteDiscards = [...this.pasteDiscards, ...fresh];
                this.noticeDismissed = false;
            }
        },
        /**
         * Paste-to-embed: a pasted provider URL lands as a link right away, and
         * the offer bar proposes replacing it with the embedded player — Tab or
         * the button embeds, Esc keeps the link.
         * @returns {boolean} True when the paste was handled here
         */
        handlePastedText(view, event) {
            const url = singlePastedUrl(event.clipboardData?.getData('text/plain'));

            if (!url || !embedHostAllowed(url, this.allowedEmbedHosts)) {
                return false;
            }

            // A provider page we cannot turn into a player URL is left as the
            // link it already is: offering to embed it would only ever frame a
            // broken box, and the link at least takes the reader to the video.
            const src = embedSrcFor(url);

            if (!src) {
                return false;
            }

            const from = view.state.selection.from;
            this.editor
                .chain()
                .focus()
                .insertContent([{ type: 'text', text: url, marks: [{ type: 'link', attrs: { href: url } }] }])
                // Autolink makes the link mark inclusive, so whatever is typed
                // next would join the address. Dropping it from the stored marks
                // leaves the caret outside the link without touching the text.
                .unsetMark('link')
                .run();

            const kind = embedHostAllowed(url, this.audioEmbedHosts) ? 'audio' : 'video';

            this.pendingEmbed = { url, src, kind, from, to: from + url.length };

            return true;
        },
        /**
         * The offer keeps pointing at the pasted URL while the operator goes on
         * typing elsewhere: its range moves with every edit, and the offer is
         * withdrawn once the URL itself is no longer there to replace.
         * @param {import('@tiptap/pm/state').Transaction} transaction - The change just applied
         */
        trackPendingEmbed(transaction) {
            if (!this.pendingEmbed || !transaction.docChanged) {
                return;
            }

            const from = transaction.mapping.map(this.pendingEmbed.from, 1);
            const to = transaction.mapping.map(this.pendingEmbed.to, -1);
            const stillThere = from < to && transaction.doc.textBetween(from, to) === this.pendingEmbed.url;
            // Writing on from the address answers the offer by itself: the two
            // biases only disagree when the change landed against one of its
            // edges, which is the reader carrying on rather than editing elsewhere.
            const wroteOnFromTheUrl =
                transaction.mapping.map(this.pendingEmbed.to, 1) !== to ||
                transaction.mapping.map(this.pendingEmbed.from, -1) !== from;

            this.pendingEmbed = stillThere && !wroteOnFromTheUrl ? { ...this.pendingEmbed, from, to } : null;
        },
        /**
         * The embed replaces the pasted address and an empty paragraph follows
         * it. The paragraph is what the caret can land in: an embed is an atom
         * with no inside, and a document ending in one would leave the selection
         * on the node itself, where the next keystroke replaces the video.
         */
        confirmPendingEmbed() {
            const { url, src, from, to } = this.pendingEmbed;
            this.pendingEmbed = null;

            this.editor
                .chain()
                .focus()
                .deleteRange({ from, to })
                .insertContentAt(from, [{ type: 'iframe', attrs: { src, title: url } }, { type: 'paragraph' }])
                .run();
        },
        keepPendingAsLink() {
            // The link is already in the document; dropping the offer is all.
            this.pendingEmbed = null;
            this.editor?.commands.focus();
        },
        /**
         * While the offer is up, Tab and Escape answer it and go no further:
         * the note editor around this input closes on Escape, and declining
         * an embed must not also throw the note away.
         */
        onEmbedOfferKeydown(event) {
            if (!this.pendingEmbed) {
                return;
            }

            if (event.key === 'Tab') {
                event.preventDefault();
                event.stopPropagation();
                this.confirmPendingEmbed();
            } else if (event.key === 'Escape') {
                event.preventDefault();
                event.stopPropagation();
                this.keepPendingAsLink();
            }
        },
        /**
         * The "HTML" toggle: entering shows the document source; leaving
         * renders the draft back through the schema (the preview), running
         * the same discard detection a paste gets so the notice can name
         * whatever the schema will not keep.
         */
        toggleHtmlMode() {
            if (!this.htmlMode) {
                this.closeUrlEntry();
                this.htmlDraft = editorHasContent(this.editor) ? this.editor.getHTML() : '';
                this.htmlMode = true;
                return;
            }

            this.registerIncomingHtml(this.htmlDraft);
            this.editor.commands.setContent(this.htmlDraft, true);
            this.htmlMode = false;
            this.editor.commands.focus();
        },
        insertImage() {
            this.askForUrl('image');
        },
        /**
         * @param {string|null} url - What the operator entered; empty applies nothing
         */
        applyImageUrl(url) {
            if (!url) {
                return;
            }

            const trimmed = url.trim();
            if (!hasSafeImageSource(trimmed)) {
                this.addPasteDiscards([{ node: 'img', reason: 'unsafe_url', detail: schemeOf(trimmed) }]);
                return;
            }

            this.editor.chain().focus().setImage({ src: trimmed }).run();
        },
        toggleLink() {
            if (this.editor.isActive('link')) {
                this.editor.chain().focus().unsetLink().run();
                return;
            }

            this.askForUrl('link');
        },
        /**
         * The URL is asked for in a row under the toolbar, not through
         * window.prompt, whose dialog introduces itself as an embedded page on
         * the reader's own domain and freezes the whole page while it is open.
         * The editor keeps its selection while the field has the focus, so the
         * link lands on the text that was selected when the button was pressed.
         * @param {'link'|'image'} kind - What the URL is for
         */
        askForUrl(kind) {
            if (!this.urlEntry) {
                this.urlEntryOpener = document.activeElement;
            }

            if (this.urlEntry !== kind) {
                this.urlDraft = '';
            }

            this.urlEntry = kind;
            this.$nextTick(() => this.$refs.urlInput?.focus());
        },
        submitUrlEntry() {
            const kind = this.urlEntry;
            const url = this.urlDraft.trim();
            this.leaveUrlEntry();

            if (kind === 'image') {
                this.applyImageUrl(url);
            } else {
                this.applyLinkUrl(url);
            }
        },
        /**
         * The focus goes back where the operator was, the button that asked or
         * the text, so a URL that applies nothing does not leave it on the page.
         */
        leaveUrlEntry() {
            const opener = this.urlEntryOpener;
            this.closeUrlEntry();

            if (opener?.isConnected && !this.editor?.view.dom.contains(opener)) {
                opener.focus();
            } else {
                this.editor?.commands.focus();
            }
        },
        closeUrlEntry() {
            this.urlEntry = null;
            this.urlEntryOpener = null;
        },
        /**
         * @param {string|null} url - What the operator entered; empty applies nothing
         */
        applyLinkUrl(url) {
            if (!url) {
                return;
            }

            this.editor.chain().focus().setLink({ href: url }).run();
        },
    },
};
</script>

<style scoped>
/* The contenteditable itself is classed through editorProps.attributes,
   with the same Tailwind vocabulary the plain textarea uses. */

/* The Placeholder extension only stamps data-placeholder on the empty
   first paragraph; drawing it is up to us. Same color the plain
   textarea's placeholder uses (theme-toolbar-icon). */
.rich-text-input__content :deep(.ProseMirror p.is-editor-empty:first-child::before) {
    content: attr(data-placeholder);
    float: left;
    height: 0;
    pointer-events: none;
    color: var(--theme-toolbar-icons-color, #9ca3af);
}

.rich-text-input__content :deep(.ProseMirror ul) {
    list-style: disc;
    padding-left: 1.25rem;
}

.rich-text-input__content :deep(.ProseMirror ol) {
    list-style: decimal;
    padding-left: 1.25rem;
}

.rich-text-input__content :deep(.ProseMirror a) {
    text-decoration: underline;
}

/* Headings, quotes and boxes as AddOnBody shows them, so the operator writes
   the hierarchy the reader will see. */
.rich-text-input__content :deep(.ProseMirror h2),
.rich-text-input__content :deep(.ProseMirror h3),
.rich-text-input__content :deep(.ProseMirror h4) {
    margin: 0.75rem 0 0.25rem;
    font-weight: 600;
    letter-spacing: -0.025em;
}

.rich-text-input__content :deep(.ProseMirror h2) {
    font-size: 1.25em;
}

.rich-text-input__content :deep(.ProseMirror h3) {
    font-size: 1.125em;
}

.rich-text-input__content :deep(.ProseMirror blockquote) {
    margin: 0.5rem 0;
    border-left: 4px solid var(--theme-border-color);
    padding-left: 1rem;
    font-style: italic;
}

.rich-text-input__content :deep(.ProseMirror aside) {
    margin: 0.5rem 0;
    border: 1px solid var(--theme-border-color);
    border-radius: 0.25rem;
    background-color: var(--theme-icons-background);
    padding: 0.75rem;
}

.rich-text-input__content :deep(.ProseMirror img) {
    max-width: 100%;
}

/* A figure as AddOnBody shows it: the media centred, the caption under it. */
.rich-text-input__content :deep(.ProseMirror figure) {
    margin: 0.5rem 0;
    max-width: 100%;
    text-align: center;
}

.rich-text-input__content :deep(.ProseMirror figcaption) {
    font-size: 0.875em;
    line-height: 1.25;
    color: var(--theme-toolbar-icons-color, #9ca3af);
}

.rich-text-input__content :deep(.ProseMirror video) {
    max-width: 100%;
    height: auto;
    pointer-events: none; /* like an embed: a block to select or delete while editing, not to play */
}

.rich-text-input__content :deep(.ProseMirror iframe) {
    max-width: 100%;
    aspect-ratio: 16 / 9;
    width: 100%;
    border: 0;
    pointer-events: none; /* the embed is a block to select/delete while editing, not to play */
}

.rich-text-input__content :deep(.ProseMirror iframe.ProseMirror-selectednode),
.rich-text-input__content :deep(.ProseMirror video.ProseMirror-selectednode) {
    outline: 2px solid var(--theme-accent-color);
}
</style>
